import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, Car, LogOut, Mail, MailOpen, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/data/site";

const TABS = [
  { id: "enquiries", label: "Enquiries", icon: MailOpen },
  { id: "bookings", label: "Cab Bookings", icon: Car },
  { id: "subscribers", label: "Newsletter", icon: Users },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const Field = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[0.65rem] uppercase tracking-[0.18em] text-[#F5F5F0]/40 font-semibold">{label}</p>
    <p className="text-sm text-[#F5F5F0]/85 mt-0.5 break-words">{children || "—"}</p>
  </div>
);

const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [overview, setOverview] = useState({ enquiries: 0, bookings: 0, subscribers: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, r] = await Promise.all([
        axios.get(`${API}/admin/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/${tab}`, { withCredentials: true }),
      ]);
      setOverview(o.data);
      setRows(r.data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (!user) return null;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen pt-28 pb-20 max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <p className="eyebrow">Admin Panel</p>
          <h1 className="font-sora text-3xl sm:text-4xl font-bold mt-2">
            Welcome back, <span className="font-playfair italic font-medium text-[#F2C230]">{user.name}</span>
          </h1>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/admin/login"); }}
          data-testid="admin-logout-button"
          className="btn-outline !py-2.5 !px-5 text-sm"
        >
          <LogOut size={15} /> Sign Out
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-10">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`glass rounded-2xl p-5 text-left transition-colors duration-300 ${
              tab === t.id ? "!border-[#F2C230]/50" : "hover:border-[#3FB6D3]/40"
            }`}
          >
            <t.icon size={18} className={tab === t.id ? "text-[#F2C230]" : "text-[#3FB6D3]"} />
            <p className="font-sora text-2xl sm:text-3xl font-extrabold gradient-text mt-3">{overview[t.id] ?? 0}</p>
            <p className="text-xs text-[#F5F5F0]/55 mt-1">{t.label}</p>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#F5F5F0]/50 text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="glass rounded-3xl p-14 text-center">
          <p className="font-sora text-lg font-semibold">Nothing here yet</p>
          <p className="text-sm text-[#F5F5F0]/55 mt-2">New {tab} will appear here as they come in.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <article key={r.id} data-testid={`admin-row-${r.id}`} className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between gap-4 mb-4">
                <p className="font-sora text-base font-semibold">
                  {tab === "subscribers" ? r.email : r.name}
                </p>
                <span className="inline-flex items-center gap-1.5 text-xs text-[#F5F5F0]/50 shrink-0">
                  <Calendar size={12} /> {fmtDate(r.created_at)}
                </span>
              </div>
              {tab === "enquiries" && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Field label="Email">{r.email}</Field>
                  <Field label="Phone">{r.phone}</Field>
                  <Field label="Service">{r.service}</Field>
                  <Field label="Message">{r.message}</Field>
                </div>
              )}
              {tab === "bookings" && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Field label="Contact">{r.email} · {r.phone}</Field>
                  <Field label="Vehicle">{r.vehicle}</Field>
                  <Field label="Route">{r.pickup} → {r.drop}</Field>
                  <Field label="Date / Pax">{r.date} · {r.passengers} pax{r.notes ? ` · ${r.notes}` : ""}</Field>
                </div>
              )}
              {tab === "subscribers" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email"><span className="inline-flex items-center gap-2"><Mail size={13} className="text-[#3FB6D3]" />{r.email}</span></Field>
                  <Field label="Source">Footer newsletter signup</Field>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </main>
  );
};

export default Admin;
